import { ScreeningQuestion } from '@/types';
import { ScreeningQuestionType } from '@/enums';

/**
 * NUMERIC screening questions store their bounds in `numericResponse`, not
 * `responses`. Strip `responses` for those before sending to the backend —
 * moved out of ProjectCreate.tsx so it can be reused by both the full-save
 * and partial-save (TELV1-490) code paths without duplicating the mapping.
 */
export const sanitizeScreeningQuestionsForPayload = (
  questions: ScreeningQuestion[],
): ScreeningQuestion[] =>
  questions.map((q) =>
    q.type === ScreeningQuestionType.NUMERIC ? { ...q, responses: undefined } : q,
  );

/**
 * Drops screener answer rows that carry no intent at all: an empty value
 * AND no rule selected. A row with either a value or a rule set is a user
 * in the middle of filling it in — that's a real validation error, not
 * scratch state, so it is left alone and surfaces the normal "please
 * select one of the rules" / "required" error instead of being silently
 * deleted (TELV1-490 architect review, Point 4).
 */
export const pruneNoIntentScreeningAnswers = (
  questions: ScreeningQuestion[] | undefined,
): ScreeningQuestion[] | undefined => {
  if (!questions) return questions;

  let didPrune = false;
  const pruned = questions.map((question) => {
    if (!Array.isArray(question.responses)) return question;

    const keptResponses = question.responses.filter(
      (response) => !(response.value?.trim() === '' && !response.rule),
    );

    if (keptResponses.length === question.responses.length) return question;
    didPrune = true;
    return { ...question, responses: keptResponses };
  });

  return didPrune ? pruned : questions;
};
